import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import InventoryItemTile from '@/components/store/InventoryItemTile'
import {
  HOME_SECTION_ROWS,
  STOREFRONT_GRID_CLASS,
  useStorefrontGridColumnCount,
} from '@/hooks/useStorefrontGridColumnCount'
import type { InventoryItemNameRow } from '@/lib/api'
import { pickClearanceDeals } from '@/lib/inventoryItemDisplay'

const MRSAI_EMPTY_IMAGE = '/mrsai-no-products.png'
const ALL_DEALS_PATH = '/items?deals=1'

function ClearanceEmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center gap-5 rounded-xl border border-stone-200/80 bg-white/80 px-6 py-10 sm:flex-row sm:justify-center sm:gap-8 sm:py-8">
      <img
        src={MRSAI_EMPTY_IMAGE}
        alt="Mr. Sai"
        className="h-36 w-auto max-h-44 shrink-0 object-contain sm:h-44"
        width={176}
        height={176}
        decoding="async"
      />
      <div className="max-w-md text-center sm:text-left">
        <p className="font-display text-lg font-semibold text-gray-800">No items found</p>
        <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{message}</p>
      </div>
    </div>
  )
}

type Props = {
  items: InventoryItemNameRow[]
  loading: boolean
  isFavorite: (id: string) => boolean
  onToggleFavorite: (id: string) => void
}

/** Home preview: up to four rows of deals; the heading opens the full list on `/items?deals=1`. */
export default function ClearanceDealsRail({
  items,
  loading,
  isFavorite,
  onToggleFavorite,
}: Props) {
  const gridCols = useStorefrontGridColumnCount('items')
  const deals = useMemo(() => pickClearanceDeals(items), [items])
  const rowItems = deals.slice(0, HOME_SECTION_ROWS * gridCols)

  const heading = (
    <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
      <h2 className="font-display text-lg font-bold text-gray-800 sm:text-xl lg:text-2xl">
        {deals.length > 0 ? (
          <Link to={ALL_DEALS_PATH} className="hover:text-primary-700">
            Clearance &amp; deals
          </Link>
        ) : (
          'Clearance & deals'
        )}
      </h2>
      {deals.length > 0 && (
        <Link
          to={ALL_DEALS_PATH}
          className="inline-flex shrink-0 items-center gap-0.5 text-sm font-semibold text-primary-700 hover:text-primary-800"
        >
          See all {deals.length}
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      )}
    </div>
  )

  if (loading) {
    return (
      <div className="w-full min-w-0">
        {heading}
        <div className={STOREFRONT_GRID_CLASS}>
          {[...Array(gridCols)].map((_, i) => (
            <div key={i} className="animate-pulse overflow-hidden rounded-xl border border-stone-200 bg-white sm:rounded-2xl">
              <div className="aspect-square bg-stone-200/70 sm:aspect-[5/4]" />
              <div className="p-2 sm:p-3">
                <div className="h-3 w-3/4 rounded bg-stone-200" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (deals.length === 0) {
    return (
      <div className="w-full min-w-0">
        {heading}
        <ClearanceEmptyState message="No clearance or deal items to show right now. Check back soon." />
      </div>
    )
  }

  return (
    <div className="w-full min-w-0" role="region" aria-label="Clearance and deals">
      {heading}
      <div className={STOREFRONT_GRID_CLASS}>
        {rowItems.map((item) => (
          <InventoryItemTile
            key={item.inventory_id}
            item={item}
            isFavorite={isFavorite(item.inventory_id)}
            onToggleFavorite={() => onToggleFavorite(item.inventory_id)}
          />
        ))}
      </div>
    </div>
  )
}
